import React, { useContext, useEffect, useState } from 'react';

import { hot } from 'react-hot-loader/root';

import RegisterCompanyForm from 'common/canny/register/RegisterCompanyForm';
import RegisterForm from 'common/canny/register/RegisterForm';
import CreateCompanyForm from 'common/company/CreateCompanyForm';
import CreateOrUpdateCompanyForm from 'common/company/CreateOrUpdateCompanyForm';
import { TrackEventContext } from 'common/containers/EventContainer';
import { LocationContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import RegisterHelmet from 'common/helmets/RegisterHelmet';

import type { TrialPlan } from 'common/api/endpoints/companies';
import type {
  RegisterOnContinueStepData,
  RegisterOnSuccessData,
} from 'common/canny/register/types';

type ConnectProps = {
  referrer: {
    name: string;
  };
};

enum RegisterStep {
  register = 'register',
  company = 'company',
}

type RegisterProps = ConnectProps;

const Register = ({ referrer }: RegisterProps) => {
  const viewer = useContext(ViewerContext);
  const location = useContext(LocationContext);
  const trackEvent = useContext(TrackEventContext);

  const [registerCompanyData, setRegisterCompanyData] = useState<RegisterOnContinueStepData>();
  const [step, setStep] = useState<RegisterStep>(RegisterStep.register);

  const trialPlan = location.query.trial as TrialPlan;

  useEffect(() => {
    trackEvent('Viewed Register Page');
    trackEvent('Intent to Register');
  }, [trackEvent]);

  const onSuccess = ({ companies, subdomain }: RegisterOnSuccessData) => {
    let redirectionURL = '';

    if (companies) {
      const company =
        companies.find((company) => !company.integrationAuthRedirectionURL) ?? companies[0];
      redirectionURL = company.integrationAuthRedirectionURL ?? getSubdomainURL(company.subdomain);
    } else if (subdomain) {
      redirectionURL = getSubdomainURL(subdomain);
    }

    window.location.assign(redirectionURL);
  };

  const onContinue = (data: RegisterOnContinueStepData) => {
    setRegisterCompanyData(data);
    setStep((prev) => (prev === RegisterStep.register ? RegisterStep.company : prev));
  };

  const renderForm = () => {
    if (viewer && viewer.companies && viewer.companies.length > 0 && trialPlan) {
      return <CreateOrUpdateCompanyForm trialPlan={trialPlan} />;
    }

    if (viewer && !viewer.loggedOut) {
      return <CreateCompanyForm onSuccess={onSuccess} trialPlan={trialPlan} />;
    }

    if (step === RegisterStep.company) {
      return (
        <RegisterCompanyForm
          email={registerCompanyData?.email}
          oauthData={registerCompanyData?.loginParams}
          trialPlan={trialPlan}
          onSuccess={onSuccess}
          onBack={() => setStep(RegisterStep.register)}
        />
      );
    }

    return (
      <RegisterForm
        referrer={referrer}
        trialPlan={trialPlan}
        onSuccess={onSuccess}
        onContinue={onContinue}
      />
    );
  };

  return (
    <>
      <RegisterHelmet />
      {renderForm()}
    </>
  );
};

const getSubdomainURL = (subdomain: string) =>
  `${window.location.protocol}//${subdomain}.${window.location.host}/admin/onboarding/redirect`;

export default hot(Register);
